import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowRight, Target, Users, TrendingUp, AlertCircle, CheckCircle, Zap } from 'lucide-react';

const ServicesPage = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('recruitment');

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'team-dynamics') setActiveTab('teamDynamics');
    else if (hash === 'leadership') setActiveTab('leadership');
    else if (hash === 'recruitment') setActiveTab('recruitment');
  }, [location.hash]);

  const services = {
    recruitment: {
      icon: Target,
      color: '#C5A059',
      title: t('services.recruitment.title'),
      tagline: 'Hire right, once.',
      problem: {
        title: 'The Problem',
        content: t('services.recruitment.problem'),
        points: [
          'Unclear role definitions lead to mis-hires',
          'Decision paralysis during hiring processes',
          'Slow onboarding reduces time-to-value',
          'High turnover costs impacting bottom line'
        ]
      },
      outcomes: {
        title: 'Outcomes',
        content: t('services.recruitment.outcomes'),
        points: [
          'Clearer role definition and success criteria',
          'Better hiring decisions under uncertainty',
          'Faster, safer onboarding processes',
          'Earlier value creation from new hires'
        ]
      },
      whatWeDo: {
        title: 'What We Do',
        content: t('services.recruitment.whatWeDo'),
        points: [
          'Audit and upgrade your recruitment processes',
          'Run alignment and engagement interviews',
          'Design and optimize onboarding journeys',
          'Implement competency-based assessment frameworks'
        ]
      }
    },
    teamDynamics: {
      icon: Users,
      color: '#1A1A1A',
      title: t('services.teamDynamics.title'),
      tagline: 'Turn groups into teams.',
      problem: {
        title: 'The Problem',
        content: t('services.teamDynamics.problem'),
        points: [
          'Unresolved conflicts creating toxic environments',
          'Poor communication causing missed deadlines',
          'Lack of accountability among team members',
          'Burnout leading to high turnover'
        ]
      },
      outcomes: {
        title: 'Outcomes',
        content: t('services.teamDynamics.outcomes'),
        points: [
          'Fewer conflicts and faster resolution',
          'Better collective decision-making',
          'Stronger accountability and mutual trust',
          'Teams that perform without burning out'
        ]
      },
      whatWeDo: {
        title: 'What We Do',
        content: t('services.teamDynamics.whatWeDo'),
        points: [
          'Conflict management workshops',
          'Team effectiveness and team-building sessions',
          'Stress and communication workshops',
          'PCM tools and behavioral assessments'
        ]
      }
    },
    leadership: {
      icon: TrendingUp,
      color: '#C5A059',
      title: t('services.leadership.title'),
      tagline: 'Align leadership for sustainable execution.',
      problem: {
        title: 'The Problem',
        content: t('services.leadership.problem'),
        points: [
          'Leadership misalignment undermining strategy',
          'Culture drift affecting execution',
          'Change resistance blocking transformation',
          'Lack of executive presence and influence'
        ]
      },
      outcomes: {
        title: 'Outcomes',
        content: t('services.leadership.outcomes'),
        points: [
          'Aligned leadership vision and style',
          'Reinforced organizational culture',
          'Sustainable execution over time',
          'Enhanced change readiness'
        ]
      },
      whatWeDo: {
        title: 'What We Do',
        content: t('services.leadership.whatWeDo'),
        points: [
          'Executive coaching and leadership assessment',
          'Organizational transformation guidance',
          'Change management implementation',
          'Leadership team alignment workshops'
        ]
      }
    }
  };

  const ServiceSection = ({ service }) => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Problem */}
      <div className="h-full">
        <div className="bg-red-50 p-6 border-l-4 border-red-500 h-full flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <h3 className="text-lg font-semibold text-[#1A1A1A]">{service.problem.title}</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{service.problem.content}</p>
          <ul className="space-y-2 mt-auto">
            {service.problem.points.map((point, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="leading-snug">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Middle Column - Outcomes */}
      <div className="h-full">
        <div className="bg-green-50 p-6 border-l-4 border-green-500 h-full flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <h3 className="text-lg font-semibold text-[#1A1A1A]">{service.outcomes.title}</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{service.outcomes.content}</p>
          <ul className="space-y-2 mt-auto">
            {service.outcomes.points.map((point, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="leading-snug">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Column - What We Do */}
      <div className="h-full">
        <div className="bg-[#1A1A1A] p-6 text-white h-full flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
            <h3 className="text-lg font-semibold">{service.whatWeDo.title}</h3>
          </div>
          <p className="text-gray-300 text-sm mb-4 leading-relaxed">{service.whatWeDo.content}</p>
          <ul className="space-y-2">
            {service.whatWeDo.points.map((point, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                <span className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="leading-snug">{point}</span>
              </li>
            ))}
          </ul>
          <Link to="/diagnosis" className="block mt-auto pt-6">
            <Button className="w-full bg-[#C5A059] hover:bg-[#D4AF61] text-[#1A1A1A]" data-testid={`service-cta-${activeTab}`}>
              {t('services.cta')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#1A1A1A] text-white" data-testid="services-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-6">
              {t('services.title')}
            </p>
            <h1 className="text-5xl md:text-6xl font-bold leading-[1.1] mb-6">
              {t('services.subtitle')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              We operate at the intersection of recruitment, leadership development, and organizational transformation to ensure sustainable execution.
            </p>
          </div>
        </div>
      </section>

      {/* Services Tabs */}
      <section className="py-24 bg-white" data-testid="services-tabs">
        <div className="container-custom">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 gap-3 bg-transparent h-auto mb-12">
              {Object.entries(services).map(([key, service]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className={`flex items-center gap-3 p-5 border-2 transition-all data-[state=active]:border-[#C5A059] data-[state=active]:bg-[#C5A059]/5 border-gray-200 bg-white hover:border-gray-300 h-auto`}
                  data-testid={`tab-${key}`}
                >
                  <service.icon className="w-6 h-6 flex-shrink-0" />
                  <div className="text-left">
                    <p className="font-semibold text-sm">{service.title}</p>
                    <p className="text-xs text-gray-500">{service.tagline}</p>
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(services).map(([key, service]) => (
              <TabsContent key={key} value={key} className="mt-0">
                <ServiceSection service={service} />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 section-alt" data-testid="services-cta">
        <div className="container-custom text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A1A1A] mb-6">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Take our Team Performance Diagnosis to identify your biggest challenges and receive personalized recommendations.
            </p>
            <Link to="/diagnosis">
              <Button className="btn-primary text-lg px-12 py-6 flex items-center gap-2 mx-auto" data-testid="services-final-cta">
                {t('hero.cta')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
